/**
 * Zerbay collection page: filter and sort sheet controller.
 *
 * Works on the markup of sections/zerbay-main-collection.liquid and snippets/zerbay-filter-sheet.liquid through the
 * data-zb-* hooks only (contract: CONTRACT.md of the collection step). Evaluated once per page; every listener is
 * delegated and added once; there are no texts in here (everything shown or announced comes from the server HTML);
 * nothing is written to the console.
 *
 * Parts: Pairs (URL pairs), Stock (the first size also ticks In stock), Live (status text), Scroll, Fragment (policy
 * for markup entering the page), Apply (change pipeline), Sheet (the dialog), Bridge (window.Zerbay.collectionFilters).
 * window.Zerbay.collectionSwitch members are called only when present.
 */
import { sectionRenderer } from '@theme/section-renderer';
import { getScrollTop, scrollTo, getScrollContainer } from '@theme/scroll-container';

const ONCE_ATTR = 'data-zb-coll-filters-js';
const OPTION_PREFIX = 'filter.v.option.';
const STOCK_NAME = 'filter.v.availability';
const MARK_SLOT = 'zbCfAuto';
const SETTLE_MS = 250;
const EXIT_MS = 320;
const MOVED_PX = 40;
const IGNORED_PARAMS = new Set(['page', 'section_id']);

const SEL = {
  coll: '[data-zb-coll]',
  results: '[data-zb-results]',
  band: '[data-zb-band]',
  pageLive: '[data-zb-live]',
  verdict: '[data-zb-say]',
  badge: '[data-zb-fs-count]',
  dialog: '[data-zb-fs]',
  form: '[data-zb-fs-form]',
  scroller: '[data-zb-fs-body]',
  sheetLive: '[data-zb-fs-live]',
  openBtn: '[data-zb-fs-open]',
  closeBtn: '[data-zb-fs-close]',
  clearBtn: '[data-zb-fs-clear]',
  sizeGroup: '[data-zb-fs-size]',
  stockGroup: '[data-zb-fs-avail]',
  chip: '[data-zb-rm]',
  jump: 'a[data-zb-go]',
};

const mqWide = window.matchMedia('(min-width: 960px)');
const mqCalm = window.matchMedia('(prefers-reduced-motion: reduce)');

/* ---------- tiny DOM helpers ---------- */
const one = (selector, scope = document) => scope.querySelector(selector);
const many = (selector, scope = document) => Array.from(scope.querySelectorAll(selector));
const dialogEl = () => one(SEL.dialog);
const formEl = () => one(SEL.form);
const resultsEl = () => one(SEL.results);
const switchApi = () => window.Zerbay?.collectionSwitch;
const shown = (el) => !!el && el.getClientRects().length > 0;
const dialogIsOpen = (d) => !!d && (d.open || d.hasAttribute('open'));
const onNextFrame = (fn) => requestAnimationFrame(fn);

function giveFocus(el) {
  if (typeof el?.focus !== 'function') return;
  /* First without scrolling; a browser that rejects the options object gets a plain call. */
  for (const how of [{ preventScroll: true }, undefined]) {
    try {
      el.focus(how);
      return;
    } catch (_) {
      /* try the next form */
    }
  }
}

function mainOpener() {
  const all = many(SEL.openBtn);
  return all.find(shown) || all[0] || null;
}

function markExpanded(flag) {
  many(SEL.openBtn).forEach((b) => b.setAttribute('aria-expanded', String(!!flag)));
}

/* Code points dropped (combining dot) or mapped (dotless i) when values are compared. */
const TURKISH_I = new Map([
  [0x307, ''],
  [0x131, 'i'],
]);

/* A URL resolved against the page, or null when it does not parse. */
function parseUrl(href) {
  try {
    return new URL(href, location.href);
  } catch (_) {
    return null;
  }
}

function composed(text) {
  try {
    return text.normalize('NFC');
  } catch (_) {
    return text;
  }
}

/* ---------- Pairs: the filter / sort state of a URL as (name, value) pairs ---------- */
const Pairs = {
  cache: null,

  isFilterName: (name) => name === STOCK_NAME || name.startsWith(OPTION_PREFIX),
  tag: (name, value) => `${name}\u0000${value}`,

  /* Comparison form of a value: NFC, lower case, and the Turkish dotted / dotless i variants mapped to plain "i". */
  simplify(text) {
    let out = '';
    for (const ch of composed(String(text)).toLowerCase()) out += TURKISH_I.get(ch.codePointAt(0)) ?? ch;
    return out;
  },

  /* Values of the chips on the page, per parameter name: the spelling the server uses. */
  chipSpelling() {
    const table = new Map();
    const scope = resultsEl();
    if (!scope) return table;
    for (const chip of many(SEL.chip, scope)) {
      const name = chip.getAttribute('data-zb-rm');
      const list = table.get(name) || [];
      list.push(chip.getAttribute('data-zb-rv') || '');
      table.set(name, list);
    }
    return table;
  },

  spell(name, value, table) {
    const list = table.get(name);
    if (!list || list.includes(value)) return value;
    const probe = Pairs.simplify(value);
    const match = list.find((v) => Pairs.simplify(v) === probe);
    return match === undefined ? value : match;
  },

  /* Query string -> list of pairs: lists split once, trimmed, de-duplicated, spelled like the chips. */
  normalise(search) {
    let params;
    try {
      params = new URLSearchParams(search);
    } catch (_) {
      return [];
    }
    const table = Pairs.chipSpelling();
    const seen = new Set();
    const pairs = [];
    params.forEach((value, name) => {
      if (!Pairs.isFilterName(name)) {
        pairs.push([name, value]);
        return;
      }
      for (const piece of value.split(',')) {
        const clean = piece.trim();
        if (!clean) continue;
        const spelled = Pairs.spell(name, clean, table);
        const id = Pairs.tag(name, spelled);
        if (seen.has(id)) continue;
        seen.add(id);
        pairs.push([name, spelled]);
      }
    });
    return pairs;
  },

  latest() {
    if (!Pairs.cache) Pairs.cache = Pairs.normalise(location.search);
    return Pairs.cache;
  },

  urlOf(pairs) {
    const params = new URLSearchParams();
    pairs.forEach(([name, value]) => params.append(name, value));
    const query = params.toString();
    return query ? `${location.pathname}?${query}` : location.pathname;
  },

  /* Pairs after an edit in the sheet. The form decides its own boxes and the sort; filter pairs without a box
     (availability 0, price, product-level) are kept; with wipe every filter pair goes. */
  fromSheet(formNode, wipe) {
    const boxed = new Set(many('input[type="checkbox"][name]', formNode).map((b) => Pairs.tag(b.name, b.value)));
    const hasSort = !!one('input[type="radio"][name="sort_by"]', formNode);
    const kept = Pairs.latest().filter(([name, value]) => {
      if (IGNORED_PARAMS.has(name)) return false;
      if (name === 'sort_by') return !hasSort;
      return !(name.startsWith('filter.') && (wipe || boxed.has(Pairs.tag(name, value))));
    });
    const have = new Set(kept.filter(([name]) => Pairs.isFilterName(name)).map(([name, value]) => Pairs.tag(name, value)));
    const live = many('input[name]', formNode).filter((i) => i.checked && !i.disabled);
    const added = live
      .filter((i) => {
        const id = Pairs.tag(i.name, i.value);
        if (!i.name.startsWith('filter.') || have.has(id)) return false;
        have.add(id);
        return true;
      })
      .map((i) => [i.name, i.value]);
    const chosen = live.filter((i) => i.name === 'sort_by').pop()?.value;
    const wantsSort = !!chosen && chosen !== (formNode.getAttribute('data-default-sort') || '');
    return wantsSort ? [...kept, ...added, ['sort_by', chosen]] : [...kept, ...added];
  },

  /* The chip a link stands for, as { name, value }; null for a link that is not a chip. */
  chipOf(anchor) {
    const name = anchor.getAttribute('data-zb-rm');
    const value = anchor.getAttribute('data-zb-rv');
    return name === null && value === null ? null : { name, value: value || '' };
  },

  /* A list without one chip pair: the exact pair if present, else the same value in another case / Unicode form. */
  without(list, chip) {
    const at = list.findIndex(([name, value]) => name === chip.name && value === chip.value);
    if (at >= 0) return list.filter((_, i) => i !== at);
    const probe = Pairs.simplify(chip.value);
    return list.filter(([name, value]) => name !== chip.name || Pairs.simplify(value) !== probe);
  },

  searchOf: (href) => parseUrl(href)?.search ?? '',

  /* Pairs after a chip / Clear all link; computed from the latest pairs, never from the link's href. */
  fromJump(anchor) {
    const survivors = Pairs.latest().filter(([name]) => !IGNORED_PARAMS.has(name));
    if (anchor.hasAttribute('data-zb-clear')) return survivors.filter(([name]) => !name.startsWith('filter.'));
    const chip = Pairs.chipOf(anchor);
    if (chip) return Pairs.without(survivors, chip);
    return Pairs.normalise(Pairs.searchOf(anchor.getAttribute('href'))).filter(([name]) => !IGNORED_PARAMS.has(name));
  },

  /* What the controls should show for a list of pairs. */
  wanted(pairs) {
    const ids = new Set();
    let sort = null;
    for (const [name, value] of pairs) {
      ids.add(Pairs.tag(name, value));
      if (name === 'sort_by') sort = value;
    }
    const f = formEl();
    return { ids, sort: sort === null ? (f ? f.getAttribute('data-default-sort') || '' : '') : sort };
  },

  paint(pairs) {
    const f = formEl();
    if (!f) return;
    const { ids, sort } = Pairs.wanted(pairs);
    for (const box of many('input[type="checkbox"][name]', f)) {
      const on = ids.has(Pairs.tag(box.name, box.value));
      box.checked = on;
      if (on) box.disabled = false;
    }
    for (const radio of many('input[type="radio"][name="sort_by"]', f)) radio.checked = radio.value === sort;
  },

  agrees(search) {
    const f = formEl();
    if (!f) return true;
    const { ids, sort } = Pairs.wanted(Pairs.normalise(search));
    const boxesOk = many('input[type="checkbox"][name]', f).every((b) => b.checked === ids.has(Pairs.tag(b.name, b.value)));
    const radios = many('input[type="radio"][name="sort_by"]', f);
    return boxesOk && radios.every((r) => r.checked === (r.value === sort));
  },
};

/* ---------- Stock: ticking the first size also ticks In stock (D20-A) ---------- */
const Stock = {
  param: '',

  /* Does the current URL still carry a pair of this size parameter and In stock? */
  holds(sizeParam) {
    return Stock.bothIn(Pairs.normalise(location.search), sizeParam);
  },

  bothIn(pairs, sizeParam) {
    return pairs.some(([n]) => n === sizeParam) && pairs.some(([n, v]) => n === STOCK_NAME && v === '1');
  },

  /* Which role a changed box plays, by the hook of its fieldset: 'stock' (the In stock box), 'size', or 'other'
     (any other box, the Out of stock box included). */
  roleOf(input) {
    if (input.closest(SEL.stockGroup)) return input.value === '1' ? 'stock' : 'other';
    return input.closest(SEL.sizeGroup) ? 'size' : 'other';
  },

  /* A shopper's tick in the sheet: the shopper taking over In stock ends the mark; a size that is the only one
     ticked in its group brings In stock along. */
  onTick(input) {
    if (input?.type !== 'checkbox') return;
    const role = Stock.roleOf(input);
    if (role === 'stock') Stock.param = '';
    else if (role === 'size' && input.checked && many('input[type="checkbox"]:checked', input.closest(SEL.sizeGroup)).length === 1) {
      Stock.autoTick(input.name);
    }
  },

  /* Tick In stock for the size parameter `name`, only when that box exists, is enabled and is not ticked yet. */
  autoTick(name) {
    const box = one(`${SEL.stockGroup} input[type="checkbox"][value="1"]`, formEl() || document);
    if (box?.disabled === false && !box.checked) {
      box.checked = true;
      Stock.param = name;
    }
  },

  /* Drop the automatic In stock once no pair of its size parameter is left. */
  settle(pairs) {
    if (!Stock.param) return pairs;
    const sizeLeft = pairs.some(([n]) => n === Stock.param);
    const stockLeft = pairs.some(([n, v]) => n === STOCK_NAME && v === '1');
    if (sizeLeft && stockLeft) return pairs;
    Stock.param = '';
    if (!stockLeft) return pairs;
    const trimmed = pairs.filter(([n, v]) => !(n === STOCK_NAME && v === '1'));
    Pairs.paint(trimmed);
    return trimmed;
  },

  /* Copy of a history state with the mark added or removed; every other key stays (scrollTop included). */
  stamp(prev) {
    const plain = !!prev && typeof prev === 'object' && !Array.isArray(prev);
    if (prev != null && !plain) return prev;
    const next = {};
    if (plain) {
      for (const k of Object.keys(prev)) if (k !== MARK_SLOT) next[k] = prev[k];
    }
    if (Stock.param) next[MARK_SLOT] = Stock.param;
    return plain || Stock.param ? next : null;
  },

  /* The record to park for a link: the mark plus the exact path + query it belongs to; null when there is no mark or
     the link leaves this path. */
  parcel(href) {
    if (!Stock.param) return null;
    const dest = parseUrl(href);
    if (!dest) return null;
    const samePath = dest.origin === location.origin && dest.pathname === location.pathname;
    return samePath ? { u: dest.pathname + dest.search, n: Stock.param } : null;
  },

  /* Before a full load to another URL of this path: park the mark for that exact path + query. */
  park(href) {
    const parcel = Stock.parcel(href);
    if (!parcel) return;
    try {
      window.sessionStorage.setItem(MARK_SLOT, JSON.stringify(parcel));
    } catch (_) {
      /* storage unavailable */
    }
  },

  /* The parked mark, consumed once; valid only for the current URL and only while its pairs are still there. */
  unpark() {
    let rec = null;
    try {
      const raw = window.sessionStorage.getItem(MARK_SLOT);
      if (raw !== null) {
        window.sessionStorage.removeItem(MARK_SLOT);
        rec = JSON.parse(raw);
      }
    } catch (_) {
      rec = null;
    }
    const sane = rec && typeof rec.n === 'string' && rec.n.startsWith(OPTION_PREFIX) && rec.u === location.pathname + location.search;
    return sane && Stock.holds(rec.n) ? rec.n : '';
  },

  /* The mark kept in the history entry, '' when there is none. */
  entryMark() {
    try {
      const mark = window.history.state?.[MARK_SLOT];
      return typeof mark === 'string' && mark.startsWith(OPTION_PREFIX) ? mark : '';
    } catch (_) {
      return '';
    }
  },

  /* Mark for the page just loaded: from the history entry, else from the parked record. */
  recall() {
    const parked = Stock.unpark();
    const entry = Stock.entryMark();
    if (entry) return Stock.holds(entry) ? entry : '';
    if (!parked) return '';
    Stock.param = parked;
    try {
      window.history.replaceState(Stock.stamp(window.history.state), '');
    } catch (_) {
      /* history state unavailable */
    }
    return parked;
  },
};

/* ---------- Fragment: the policy for markup that enters the live page ---------- */
const Fragment = {
  forbidden: new Set(['script', 'noscript', 'iframe', 'frame', 'object', 'embed', 'base', 'meta', 'link', 'style', 'template', 'animate', 'set', 'animatemotion', 'animatetransform']),
  urlAttrs: new Set(['href', 'src', 'action', 'poster', 'xlink:href', 'data', 'cite', 'background']),
  setAttrs: new Set(['srcset', 'imagesrcset']),

  /* Is this target allowed? Web and relative targets always; mailto / tel / data:image only when the caller says so. */
  targetOk(value, extra) {
    const raw = String(value);
    let scheme;
    try {
      scheme = new URL(raw, location.href).protocol;
    } catch (_) {
      return false;
    }
    if (scheme === 'http:' || scheme === 'https:') return true;
    if (!extra) return false;
    if (scheme === 'mailto:' || scheme === 'tel:') return extra.contact === true;
    return scheme === 'data:' && extra.image === true && /^data:image\//i.test(Array.from(raw).filter((c) => c.charCodeAt(0) > 32).join(''));
  },

  /* srcset / imagesrcset split by the HTML candidate rules: a URL is a run of non-space characters (commas allowed
     inside); a trailing comma ends a candidate without descriptors; otherwise it ends at the next top-level comma. */
  candidateUrls(text) {
    const urls = [];
    const gap = (c) => ' \t\n\f\r'.includes(c);
    let i = 0;
    while (i < text.length) {
      while (i < text.length && (gap(text[i]) || text[i] === ',')) i += 1;
      if (i >= text.length) break;
      let j = i;
      while (j < text.length && !gap(text[j])) j += 1;
      const word = text.slice(i, j);
      i = j;
      urls.push(word.replace(/,+$/, ''));
      if (word.endsWith(',')) continue;
      let inParens = false;
      while (i < text.length) {
        const ch = text[i];
        i += 1;
        if (ch === '(') inParens = true;
        else if (ch === ')') inParens = false;
        else if (ch === ',' && !inParens) break;
      }
    }
    return urls;
  },

  styleOk(value) {
    const text = String(value);
    if (text.includes('\\')) return false;
    if (/expression\s*\(|@import|javascript:|image-set\s*\(/i.test(text)) return false;
    let from = 0;
    for (;;) {
      const at = text.toLowerCase().indexOf('url(', from);
      if (at < 0) return true;
      const rest = text.slice(at + 4);
      const end = rest.indexOf(')');
      const target = (end < 0 ? rest : rest.slice(0, end)).replace(/^[\s"']+|[\s"']+$/g, '');
      if (!Fragment.targetOk(target, null)) return false;
      from = at + 4;
    }
  },

  scrub(el) {
    for (const attr of Array.from(el.attributes)) {
      const nm = attr.name.toLowerCase();
      let drop = false;
      if (nm.startsWith('on') || nm === 'srcdoc' || nm === 'formaction') drop = true;
      else if (Fragment.urlAttrs.has(nm)) drop = !Fragment.targetOk(attr.value, { contact: true, image: nm === 'src' });
      else if (Fragment.setAttrs.has(nm)) drop = !Fragment.candidateUrls(attr.value).every((u) => Fragment.targetOk(u, null));
      else if (nm === 'style') drop = !Fragment.styleOk(attr.value);
      if (drop) el.removeAttribute(attr.name);
    }
  },

  /* Cleans an Element or DocumentFragment in place and returns it; null for anything else or a forbidden root. */
  sanitize(node) {
    if (!node || (node.nodeType !== 1 && node.nodeType !== 11)) return null;
    if (node.nodeType === 1 && Fragment.forbidden.has(node.localName.toLowerCase())) return null;
    many('*', node).forEach((el) => {
      if (Fragment.forbidden.has(el.localName.toLowerCase())) el.remove();
    });
    if (node.nodeType === 1) Fragment.scrub(node);
    many('*', node).forEach((el) => Fragment.scrub(el));
    return node;
  },

  /* A link taken from a section render loses the section-rendering parameter. */
  tidyLink(anchor) {
    const raw = anchor.getAttribute('href');
    if (!raw || !raw.includes('section_id')) return;
    let target;
    try {
      target = new URL(raw, location.href);
    } catch (_) {
      return;
    }
    if (!target.searchParams.has('section_id')) return;
    const keep = new URLSearchParams();
    target.searchParams.forEach((v, n) => {
      if (n !== 'section_id') keep.append(n, v);
    });
    target.search = keep.toString();
    anchor.setAttribute('href', target.origin === location.origin ? `${target.pathname}${target.search}${target.hash}` : target.href);
  },

  copyBadge(sourceRoot) {
    const mine = one(SEL.badge);
    const theirs = sourceRoot?.querySelector(SEL.badge);
    if (!mine || !theirs) return;
    mine.textContent = theirs.textContent;
    mine.hidden = theirs.hasAttribute('hidden');
  },
};

/* ---------- Panel state shared by Sheet, Apply and Live ----------
   `visit` is the state of one open of the sheet: it exists from the open until the close has finished, and is taken
   over by Sheet.finish in one step.
     { opener, dirty, held, watch, exitOpts, noModal, muted }
   opener: the button that opened it; dirty: a change landed while open; held: status text kept during the closing
   animation; watch: the running exit watch (its presence means "closing"); exitOpts: merged close options;
   noModal: the fallback is in use; muted: the surroundings made inert by the fallback. */
const panel = {
  visit: null,
  pristine: true,
  /* scroll after a change */
  scrollAfter: false,
  closedAt: 0,
};

const sheetOpen = () => panel.visit !== null;
const sheetClosing = () => !!panel.visit?.watch;

/* ---------- Live: the status text for screen readers ---------- */
const Live = {
  ticket: 0,

  region() {
    const box = dialogEl();
    return sheetOpen() && box ? one(SEL.sheetLive, box) : one(SEL.pageLive);
  },

  /* Drop everything still waiting to be announced. */
  forget() {
    Live.ticket += 1;
    if (panel.visit) panel.visit.held = null;
  },

  /* A status that arrives during the exit animation is parked on the visit; finish() reads it out in the page region. */
  hold(text) {
    panel.visit.held = text;
  },

  announce(text) {
    const mine = ++Live.ticket;
    if (sheetClosing()) {
      Live.hold(text);
      return;
    }
    const first = Live.region();
    if (first) first.textContent = '';
    if (first && text) Live.write(text, mine, first);
  },

  /* The text goes in a frame after the region was emptied, into the region that is current at that moment; when that
     is another region it is emptied first and the write waits one more frame. */
  write(text, mine, emptied) {
    onNextFrame(() => {
      if (mine !== Live.ticket) return;
      if (sheetClosing()) {
        Live.hold(text);
        return;
      }
      const here = Live.region();
      if (!here) return;
      if (here === emptied) {
        here.textContent = text;
        return;
      }
      here.textContent = '';
      Live.write(text, mine, here);
    });
  },
};

/* ---------- Scroll ---------- */
const Scroll = {
  /* Put the top of the results just under the band (the band itself when it is not sticky). */
  toResults() {
    let anchorEl = resultsEl();
    if (!anchorEl) return;
    const band = one(SEL.band);
    if (band && getComputedStyle(band).position === 'static') anchorEl = band;
    const offset = Math.max(0, (parseFloat(getComputedStyle(getScrollContainer()).scrollPaddingTop) || 0) - 8);
    const distance = anchorEl.getBoundingClientRect().top;
    if (distance >= offset) return;
    scrollTo({ top: Math.max(0, getScrollTop() + distance - offset), behavior: 'instant' });
  },

  /* Phone band: slide the current link into view inside its own row; the page stays where it is. */
  revealCurrent() {
    const link = one(`${SEL.band} [aria-current="page"]`);
    const row = link?.closest('.zb-band__list, ul');
    const overflows = !!row && row.scrollWidth > row.clientWidth;
    if (!overflows) return;
    const style = getComputedStyle(row);
    if (style.direction === 'rtl') {
      /* RTL: scrollLeft is 0 at the right edge and negative towards the end; the start padding is on the right */
      const start = parseFloat(style.paddingInlineStart) || parseFloat(style.paddingRight) || 0;
      const back = link.getBoundingClientRect().right - (row.getBoundingClientRect().right - start);
      row.scrollTo({ left: Math.min(0, row.scrollLeft + back), behavior: 'instant' });
      return;
    }
    const lead = parseFloat(style.paddingLeft) || 0;
    row.scrollTo({ left: Math.max(0, link.offsetLeft - lead), behavior: 'instant' });
  },
};

/* ---------- Apply: from a change to the swapped page ---------- */
const Apply = {
  gen: 0,
  waitId: 0,
  ctl: null,
  flying: false,

  /* Ends the pending timer and the request in flight. The sheet's scroll flags are left to the caller. */
  halt() {
    Apply.gen += 1;
    clearTimeout(Apply.waitId);
    Apply.waitId = 0;
    if (Apply.ctl) {
      try {
        Apply.ctl.abort();
      } catch (_) {
        /* finished already */
      }
      Apply.ctl = null;
    }
    if (Apply.flying) {
      Apply.flying = false;
      resultsEl()?.removeAttribute('aria-busy');
    }
  },

  /* Bridge cancel: halt, and forget the scroll that was to follow. */
  cancelAll() {
    Apply.halt();
    panel.scrollAfter = false;
    if (panel.visit) panel.visit.dirty = false;
  },

  /* origin 'sheet': the response may scroll to the results (at close, or at once when the sheet is shut already).
     origin 'link': never scrolls. */
  begin(pairs, delayed, origin) {
    if (switchApi()?.isTraversing?.()) return;
    Apply.halt();
    const mine = Apply.gen;
    switchApi()?.revertPending?.();
    Pairs.cache = pairs;
    panel.scrollAfter = origin === 'sheet' && sheetOpen();
    if (!delayed) {
      Apply.fetchPage(mine, pairs, origin);
      return;
    }
    Apply.waitId = setTimeout(() => {
      Apply.waitId = 0;
      if (mine === Apply.gen) Apply.fetchPage(mine, pairs, origin);
    }, SETTLE_MS);
  },

  bail(url) {
    Apply.flying = false;
    Apply.ctl = null;
    resultsEl()?.removeAttribute('aria-busy');
    Stock.park(url);
    location.assign(url);
  },

  async fetchPage(mine, pairs, origin) {
    if (mine !== Apply.gen) return;
    const url = Pairs.urlOf(pairs);
    const holder = resultsEl();
    const sectionId = one(SEL.coll)?.getAttribute('data-section-id');
    if (!holder || !sectionId || typeof DOMParser === 'undefined' || !sectionRenderer?.getSectionHTML) {
      Apply.bail(url);
      return;
    }
    const ctl = new AbortController();
    Apply.ctl = ctl;
    Apply.flying = true;
    holder.setAttribute('aria-busy', 'true');
    try {
      /* Pass a fresh URL (getSectionHTML writes section_id into it); it does not check the HTTP status. Its private
         cache keeps every response for the page's life and a pending request started without a signal is joined;
         the generation number covers both. */
      const html = await sectionRenderer.getSectionHTML(sectionId, false, new URL(url, location.origin), ctl.signal);
      if (mine !== Apply.gen) return;
      Apply.ctl = null;
      Apply.flying = false;
      if (typeof html !== 'string' || !html) {
        Apply.bail(url);
        return;
      }
      Apply.land(html, url, origin);
    } catch (err) {
      if (err?.name === 'AbortError' || mine !== Apply.gen) return;
      Apply.bail(url);
    }
  },

  /* Keys of the chips that come after `chip`, in page order. */
  chipsAfter(chip, holder) {
    if (!chip.hasAttribute('data-zb-rm')) return [];
    const all = many(SEL.chip, holder);
    return all.slice(all.indexOf(chip) + 1).map((c) => Pairs.tag(c.getAttribute('data-zb-rm'), c.getAttribute('data-zb-rv') || ''));
  },

  /* Where focus is right now, so it can be put back after the children are replaced. */
  noteFocus(scroller, holder) {
    const active = document.activeElement;
    if (!active) return null;
    if (scroller?.contains(active) && active.name) return { zone: 'sheet', name: active.name, value: active.value };
    if (!holder.contains(active)) return null;
    return { zone: 'results', later: Apply.chipsAfter(active, holder) };
  },

  /* In the sheet: the same box, else the first usable one of that name, else the X. */
  sheetTarget(memo, scroller, box) {
    const usable = many('input', scroller).filter((i) => i.name === memo.name && !i.disabled);
    const pick = usable.find((i) => i.value === memo.value) || usable.find((i) => i.type !== 'radio' || i.checked);
    return pick || (box ? one(SEL.closeBtn, box) : null);
  },

  /* In the results: the next chip that still exists, else the Filter button. */
  resultsTarget(memo, holder) {
    const chips = many(SEL.chip, holder);
    const byKey = new Map(chips.map((c) => [Pairs.tag(c.getAttribute('data-zb-rm'), c.getAttribute('data-zb-rv') || ''), c]));
    const next = memo.later.find((key) => byKey.has(key));
    return next === undefined ? mainOpener() : byKey.get(next);
  },

  putFocusBack(memo, scroller, holder, box) {
    let target = null;
    if (memo?.zone === 'sheet' && scroller) target = Apply.sheetTarget(memo, scroller, box);
    else if (memo?.zone === 'results') target = Apply.resultsTarget(memo, holder);
    giveFocus(target);
    const here = document.activeElement;
    const stranded = !here || here === document.body || !here.isConnected;
    if (stranded) giveFocus(sheetOpen() && box ? one(SEL.closeBtn, box) : mainOpener());
  },

  /* After a swap: with the sheet open a sheet change is remembered (the close scrolls); with the sheet shut, a change
     that started in the sheet scrolls now unless the shopper moved the page meanwhile. */
  settleScroll(origin, stayed) {
    const waiting = panel.scrollAfter;
    panel.scrollAfter = false;
    if (panel.visit) {
      if (origin === 'sheet') panel.visit.dirty = true;
    } else if (waiting && stayed) {
      Scroll.toResults();
    }
  },

  /* Replace the children of the results holder and of the sheet scroller with the response's. */
  land(html, url, origin) {
    const incoming = new DOMParser().parseFromString(html, 'text/html');
    many('noscript', incoming).forEach((n) => n.remove());
    const freshResults = incoming.querySelector(SEL.results);
    const holder = resultsEl();
    if (!freshResults || !holder) {
      Apply.bail(url);
      return;
    }
    many('a[href]', incoming).forEach((a) => Fragment.tidyLink(a));
    const box = dialogEl();
    const scroller = box ? one(SEL.scroller, box) : null;
    const freshScroller = incoming.querySelector(SEL.scroller);
    if (Fragment.sanitize(freshResults) === null || (freshScroller && Fragment.sanitize(freshScroller) === null)) {
      Apply.bail(url);
      return;
    }

    const memo = Apply.noteFocus(scroller, holder);
    const scrolled = scroller?.scrollTop ?? 0;
    /* The size group is recognised from product data, so a response without products lacks its mark: remember the
       input name and put the mark back on the group holding it. */
    const sizeParam = scroller?.querySelector(`${SEL.sizeGroup} input[name]`)?.name ?? '';
    /* Sampled before the swap: has the page moved more than 40 px since the sheet closed? */
    const stayed = Math.abs(getScrollTop() - panel.closedAt) <= MOVED_PX;

    holder.replaceChildren(...Array.from(freshResults.childNodes));
    holder.removeAttribute('aria-busy');
    if (scroller && freshScroller) {
      scroller.replaceChildren(...Array.from(freshScroller.childNodes));
      scroller.scrollTop = scrolled;
      if (sizeParam && !scroller.querySelector(SEL.sizeGroup)) {
        const same = many('input[name]', scroller).find((i) => i.name === sizeParam);
        same?.closest('fieldset')?.setAttribute('data-zb-fs-size', '');
      }
    }
    Fragment.copyBadge(incoming);
    try {
      window.history.replaceState(Stock.stamp(window.history.state), '', url);
    } catch (_) {
      /* history state unavailable */
    }
    switchApi()?.commit?.();
    Apply.settleScroll(origin, stayed);
    Apply.putFocusBack(memo, scroller, holder, box);
    Live.announce(holder.querySelector(SEL.verdict)?.textContent.trim() || '');
  },
};

/* The things the sheet needs when it is not a native modal. */
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';

/* The watch over one closing animation: one token per close. Its two triggers (the dialog's own animationend and a
   timer) both end in exitDone, which acts only while this token is still the one on the visit. */
function startExitWatch(visit, box) {
  const watch = { stop: new AbortController(), timer: 0 };
  visit.watch = watch;
  box.addEventListener('animationend', (ev) => ev.target === box && exitDone(watch), { signal: watch.stop.signal });
  watch.timer = setTimeout(() => exitDone(watch), EXIT_MS);
}

function endExitWatch(watch) {
  watch.stop.abort();
  clearTimeout(watch.timer);
}

function exitDone(watch) {
  if (panel.visit?.watch === watch) Sheet.finish(panel.visit.exitOpts);
}

/* ---------- Sheet: the dialog ---------- */
const Sheet = {
  /* Fallback only: everything around the dialog becomes inert and hidden; the old values are returned to be put back. */
  muteSurroundings(box) {
    const muted = [];
    for (let el = box; el && el !== document.body && el.parentElement; el = el.parentElement) {
      for (const neighbour of Array.from(el.parentElement.children)) {
        if (neighbour === el || ['SCRIPT', 'STYLE'].includes(neighbour.tagName)) continue;
        muted.push({ el: neighbour, inert: neighbour.hasAttribute('inert'), aria: neighbour.getAttribute('aria-hidden') });
        neighbour.setAttribute('inert', '');
        neighbour.setAttribute('aria-hidden', 'true');
      }
    }
    return muted;
  },

  unmuteSurroundings(muted) {
    for (const rec of muted || []) {
      if (!rec.inert) rec.el.removeAttribute('inert');
      if (rec.aria === null) rec.el.removeAttribute('aria-hidden');
      else rec.el.setAttribute('aria-hidden', rec.aria);
    }
  },

  /* Native modal if the browser has one; false means the fallback must be used. */
  raise(box) {
    if (typeof box.showModal !== 'function') return false;
    try {
      box.showModal();
      return true;
    } catch (_) {
      return false;
    }
  },

  /* No native modal: the dialog shows by its attribute, is marked modal for screen readers, and the page around it
     becomes inert until finish() puts it back. */
  openWithoutModal(box, visit) {
    box.setAttribute('open', '');
    box.setAttribute('aria-modal', 'true');
    box.classList.add('is-fallback');
    visit.muted = Sheet.muteSurroundings(box);
  },

  /* The dialog element back to its shut state. */
  dismantle(box) {
    if (!box) return;
    box.classList.remove('is-closing', 'is-fallback');
    if (box.open && typeof box.close === 'function') {
      try {
        box.close();
      } catch (_) {
        /* the attribute below does the job */
      }
    }
    box.removeAttribute('open');
    box.removeAttribute('aria-modal');
  },

  /* Final teardown; safe to call twice. The visit is taken over and cleared before dialog.close(), so the dialog's
     close handler sees no visit and does not run a second teardown. Order: dialog, surroundings, scroll lock,
     results scroll, held status, focus. */
  finish(options) {
    const visit = panel.visit;
    panel.visit = null;
    if (visit?.watch) endExitWatch(visit.watch);
    Sheet.dismantle(dialogEl());
    Sheet.unmuteSurroundings(visit?.muted);
    document.documentElement.classList.remove('zb-scroll-lock');
    markExpanded(false);
    if (visit?.dirty) Scroll.toResults();
    if (panel.scrollAfter) panel.closedAt = getScrollTop();
    if (visit?.held != null) Live.announce(visit.held);
    if (!visit || options?.noFocus) return;
    giveFocus(shown(visit.opener) ? visit.opener : mainOpener());
  },

  close(options) {
    const opts = options || {};
    const visit = panel.visit;
    const box = dialogEl();
    if (!visit || !box || !dialogIsOpen(box)) {
      if (visit || dialogIsOpen(box)) Sheet.finish(opts);
      return;
    }
    if (visit.watch) {
      /* A close is running already: later options are merged into it (noFocus and instant win). */
      visit.exitOpts = { noFocus: !!(visit.exitOpts?.noFocus || opts.noFocus), instant: !!opts.instant };
      if (opts.instant) Sheet.finish(visit.exitOpts);
      return;
    }
    if (opts.instant || mqCalm.matches) {
      Sheet.finish(opts);
      return;
    }
    visit.exitOpts = { noFocus: !!opts.noFocus, instant: false };
    box.classList.add('is-closing');
    startExitWatch(visit, box);
  },

  show(button) {
    const box = dialogEl();
    if (!box) return;
    const api = switchApi();
    if (api?.isTraversing?.()) return;
    if (api?.hasPending?.()) api.cancelPending?.();
    /* A tap while the previous visit is still animating out ends that visit now; its parked status is read out in the new
       visit's region. */
    const closing = sheetClosing() ? panel.visit : null;
    const carried = closing ? closing.held : null;
    if (closing) {
      closing.held = null;
      Sheet.finish({ noFocus: true });
    }
    if (dialogIsOpen(box)) return;
    /* Everything that says "open" (the visit, the scroll lock, the expanded buttons) is in place before showModal():
       its focusing steps run focus listeners synchronously, and those must already see an open sheet. If one of them
       closed the sheet again (at once, or by starting the exit), this open stops here; finish() undoes the rest. */
    const visit = { opener: button || null, dirty: false, held: null, watch: null, exitOpts: null, noModal: false, muted: null };
    panel.visit = visit;
    box.classList.remove('is-closing');
    document.documentElement.classList.add('zb-scroll-lock');
    markExpanded(true);
    visit.noModal = !Sheet.raise(box);
    if (panel.visit !== visit || visit.watch) {
      if (carried !== null) Live.announce(carried);
      return;
    }
    if (visit.noModal) Sheet.openWithoutModal(box, visit);
    else box.classList.remove('is-fallback');
    const scroller = one(SEL.scroller, box);
    if (scroller && panel.pristine) {
      scroller.scrollTop = 0;
      panel.pristine = false;
    }
    giveFocus(one(SEL.closeBtn, box));
    if (carried !== null) Live.announce(carried);
  },

  untickAll() {
    const f = formEl();
    if (!f) return;
    many('input[type="checkbox"]', f).forEach((b) => {
      b.checked = false;
    });
    Apply.begin(Stock.settle(Pairs.fromSheet(f, true)), false, 'sheet');
  },

  /* Keep Tab inside the dialog (fallback only): leaving the first (Shift+Tab) or last stop wraps to the other end. */
  keepTabInside(ev, box) {
    const stops = many(FOCUSABLE, box).filter(shown);
    if (!stops.length) return;
    const [leaving, entering] = ev.shiftKey ? [stops[0], stops[stops.length - 1]] : [stops[stops.length - 1], stops[0]];
    const here = document.activeElement;
    if (here !== leaving && box.contains(here)) return;
    ev.preventDefault();
    giveFocus(entering);
  },
};

/* ---------- Bridge ---------- */
function resetFromUrl(options) {
  const opts = options || {};
  Pairs.cache = null;
  panel.pristine = true;
  if (panel.visit) panel.visit.dirty = false;
  Stock.param = opts.restoreAuto ? Stock.recall() : '';
}

function installBridge() {
  window.Zerbay = window.Zerbay || {};
  window.Zerbay.collectionFilters = Object.freeze({
    cancel: Apply.cancelAll,
    pending: () => !!Apply.waitId || Apply.flying,
    closeSheet: Sheet.close,
    isSheetOpen: sheetOpen,
    resetFromUrl,
    syncForm: () => Pairs.paint(Pairs.normalise(location.search)),
    controlsMatch: Pairs.agrees,
    toResults: Scroll.toResults,
    announce: Live.announce,
    sanitize: Fragment.sanitize,
    copyCount: Fragment.copyBadge,
  });
}

/* ---------- Listeners (delegated, added once) ---------- */
const traversing = () => !!switchApi()?.isTraversing?.();

/* The sheet's own buttons: the first selector the click falls inside decides. */
const BUTTON_ROUTES = [
  [SEL.openBtn, (button) => Sheet.show(button)],
  [SEL.closeBtn, () => Sheet.close()],
  [
    SEL.clearBtn,
    () => {
      if (!traversing()) Sheet.untickAll();
    },
  ],
];

/* A same-tab link inside the results that is not one of the filter links (a page number). */
function isPageLink(link) {
  if (!link || link.hasAttribute('data-zb-go')) return false;
  return !!resultsEl()?.contains(link) && ['', '_self'].includes(link.target);
}

function onClick(ev) {
  const node = ev.target instanceof Element ? ev.target : null;
  if (!node) return;
  for (const [selector, run] of BUTTON_ROUTES) {
    const hit = node.closest(selector);
    if (!hit) continue;
    ev.preventDefault();
    run(hit);
    return;
  }
  const plain = ev.button === 0 && !(ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey);
  const jump = node.closest(SEL.jump);
  if (jump) {
    if (!plain) return;
    ev.preventDefault();
    if (traversing()) return;
    const pairs = Pairs.fromJump(jump);
    Pairs.paint(pairs);
    Apply.begin(Stock.settle(pairs), false, 'link');
    return;
  }
  /* A page number: the D20-A mark travels along. */
  if (Stock.param && !ev.defaultPrevented && plain) {
    const link = node.closest('a[href]');
    if (isPageLink(link)) Stock.park(link.href);
  }
  const box = dialogEl();
  if (!sheetOpen() || !box) return;
  /* A click on the backdrop (the dialog itself) closes; in the fallback so does any click outside it. */
  if (node === box || (panel.visit.noModal && !box.contains(node))) {
    if (node !== box) ev.preventDefault();
    Sheet.close();
  }
}

function onChange(ev) {
  const field = ev.target;
  const formNode = field?.closest ? field.closest(SEL.form) : null;
  if (!formNode || traversing()) return;
  Stock.onTick(field);
  Apply.begin(Stock.settle(Pairs.fromSheet(formNode, false)), true, 'sheet');
}

function onSubmit(ev) {
  const formNode = ev.target;
  if (!formNode?.matches || !formNode.matches(SEL.form)) return;
  ev.preventDefault();
  if (traversing()) return;
  Apply.begin(Stock.settle(Pairs.fromSheet(formNode, false)), false, 'sheet');
}

/* Keys that matter while the fallback is open. */
const FALLBACK_KEYS = new Map([
  ['Escape', 'leave'],
  ['Esc', 'leave'],
  ['Tab', 'wrap'],
]);

function onKeydown(ev) {
  const action = FALLBACK_KEYS.get(ev.key);
  const box = action && panel.visit?.noModal ? dialogEl() : null;
  if (!box) return;
  if (action === 'wrap') {
    Sheet.keepTabInside(ev, box);
    return;
  }
  ev.preventDefault();
  Sheet.close();
}

/* cancel / close events of the sheet's own dialog (they do not bubble, hence the capture listeners below). */
const ownDialog = (ev) => {
  const box = dialogEl();
  return box && ev.target === box ? box : null;
};

/* An Esc the browser lets us veto becomes our own (animated) close. */
function onCancel(ev) {
  if (!ownDialog(ev) || !ev.cancelable) return;
  ev.preventDefault();
  Sheet.close();
}

/* The browser closed the dialog itself (an Esc that could not be vetoed): finish what is left. */
function onDialogClosed(ev) {
  const box = ownDialog(ev);
  if (box && !box.open && panel.visit) Sheet.finish(panel.visit.exitOpts);
}

/* Drop a sheet that is open (or closing) without moving focus. */
function dropSheet() {
  if (panel.visit || dialogIsOpen(dialogEl())) Sheet.finish({ noFocus: true });
}

function sectionTouchesUs(target) {
  return !!target?.querySelector && (target.matches(SEL.coll) || !!target.querySelector(SEL.coll));
}

function wire() {
  const d = document;
  d.addEventListener('click', onClick);
  d.addEventListener('change', onChange);
  d.addEventListener('submit', onSubmit);
  d.addEventListener('keydown', onKeydown);

  d.addEventListener('cancel', onCancel, true);
  d.addEventListener('close', onDialogClosed, true);

  /* Back/forward cache: leaving the page drops pending work; coming back drops held status, shuts the sheet and
     re-reads mark and controls from the URL. */
  window.addEventListener('pagehide', () => Apply.cancelAll());
  window.addEventListener('pageshow', (ev) => {
    if (!ev.persisted) return;
    Apply.cancelAll();
    Live.forget();
    dropSheet();
    resetFromUrl({ restoreAuto: true });
    Pairs.paint(Pairs.normalise(location.search));
  });

  mqWide.addEventListener('change', () => {
    if (sheetOpen()) Sheet.close({ instant: true });
  });

  /* Theme editor: unloading the section halts everything in progress; loading it again starts from the URL. */
  d.addEventListener('shopify:section:unload', (ev) => {
    if (!sectionTouchesUs(ev.target)) return;
    Apply.cancelAll();
    switchApi()?.revertPending?.();
    Live.forget();
    dropSheet();
    Pairs.cache = null;
  });
  d.addEventListener('shopify:section:load', (ev) => {
    if (!sectionTouchesUs(ev.target)) return;
    resetFromUrl({ restoreAuto: true });
    Scroll.revealCurrent();
  });

  window.addEventListener('load', Scroll.revealCurrent);
}

/* One evaluation per page: a second module URL does nothing. */
if (!document.documentElement.hasAttribute(ONCE_ATTR)) {
  document.documentElement.setAttribute(ONCE_ATTR, '');
  installBridge();
  wire();
  Stock.param = Stock.recall();
  Scroll.revealCurrent();
}
